import type React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { Button, type ButtonProps, Grid, Stack, Typography, useMediaQuery } from '@mui/material';
import Chip2 from '@mui/material/Unstable_Chip2';
import type { Flow } from '@/constants/flow';
import { useCustom } from '@/hooks/use-custom';
import { useFlow } from '@/hooks/use-flow';

interface FlowerBoxProps {
  title?: string | React.ReactNode;
  account?: string;
  children?: React.ReactNode;
  backFlow?: Flow;
  backButtonProps?: ButtonProps;
}

export const FlowerBox: React.FC<FlowerBoxProps> = ({ title, account, children, backFlow, backButtonProps }) => {
  const { t, i18n } = useTranslation();
  const { setFlow } = useFlow();
  const {
    layoutConfig,
    componentConfig: { banner },
  } = useCustom();
  const isAr = i18n.language.toLowerCase() === 'ar';
  const isUpLarge = useMediaQuery((theme) => theme.breakpoints.up('lg'));
  const showLogo = layoutConfig?.layout !== 'space-between';
  const { images } = banner || {};
  const showImageGallery = images && images.length > 0;
  const showBlankBackFlow = backFlow || (showLogo && isUpLarge) || (showImageGallery && !showLogo && isUpLarge);

  return (
    <Grid
      container
      direction="column"
      size={{
        xs: 12,
        lg: isUpLarge ? 6 : 12,
      }}
      sx={(theme: any) => ({
        padding: { sm: theme.spacing(6), xs: theme.spacing(3) },
        paddingTop: { lg: theme.spacing(6), md: 0, sm: 0, xs: 0 },
      })}
      className="flower-box-container"
    >
      <Grid
        size={12}
        sx={(theme) => ({
          ...(showBlankBackFlow && theme.mixins.headerPlaceholder),
          mb: showBlankBackFlow ? 3 : 0,
        })}
      >
        {backFlow && (
          <Stack spacing={2} direction="row" justifyContent="space-between">
            <Button
              size="small"
              color="inherit"
              startIcon={isAr ? <ChevronRight /> : <ChevronLeft />}
              onClick={() => setFlow(backFlow)}
              {...backButtonProps}
            >
              {t('back')}
            </Button>

            {account && (
              <Chip2 label={account} variant="outlined" title={account} className="truncate" sx={{ maxWidth: '50%' }} />
            )}
          </Stack>
        )}
      </Grid>

      <Grid
        id="step-box-container"
        tabIndex={-1}
        sx={{
          outline: 'none',
        }}
        size={12}
      >
        <Stack spacing={2}>
          {title && (
            <Typography
              variant="pageTitle"
              component="h2"
              sx={{
                fontWeight: 600,
                pb: 1,
                wordBreak: 'break-word',
              }}
            >
              {title}
            </Typography>
          )}

          {children}
        </Stack>
      </Grid>
    </Grid>
  );
};
